import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useConsole, useRead } from './context';
import { Empty, Failure, Loading } from './bits';
import { Chip, Segmented } from './kit';
import type { ChipTone } from './kit';
import { useSettled } from './business/activity/useSettled';
import { trackIndex } from './business/BusinessHeader';
import { nextLine, nextStepOf, reasonLabel } from './business/activity/next';
import { stageName } from '../vocabulary';
import { useT } from '../messages';
import type { T } from '../messages';
import type { FleetTenant, HealthVerdict, IntakeSummary, TenantHealth } from '../wire';
import './business/activity/activity.css';

/**
 * **The businesses** — one row each, answering three questions in order (console
 * redesign, phase B6; the mockup's `v-fleet`): who is it, where are they in
 * getting live and what is next, and is anything wrong.
 *
 *  * **Who** — the name first, the id small beneath it with the place (the
 *    timezone's city: the tenant record has no address) and a *demo* or *test*
 *    marker, so a fixture is never mistaken for a customer.
 *  * **Stage** — Setting up, Moving their book in, Live, as words over a
 *    four-dash bar that is the business page's stage track in miniature. The
 *    fourth dash, "taking bookings online", is a readiness milestone nothing
 *    computes yet, so it is never lit (the header's rule, not the mockup's).
 *  * **Next** — one line from `nextStepOf`: "Fix: …", "Waiting on the owner to
 *    finish their questionnaire", "Nothing to do". A pure function of the fleet
 *    row, the intake chip and health, so it can switch to the readiness read
 *    when there is one without this screen changing.
 *  * **Health** — a chip ("Nothing wrong", "2 problems") with the reasons, in
 *    the owner's terms, on hover and on keyboard focus; and **Protected** beside
 *    it for a real business nothing may wipe.
 *
 * Module lists, key counts and endpoint counts left this screen for the
 * business page, where they mean something; what they were for — "is a partner
 * cut off?" — is in the health chip and the Next line.
 *
 * What is **not** here: any count of guests, bookings or money. Those are the
 * business's book, and this surface holds no grant on it. Health is counts and
 * instants about queues and rows, never about a person.
 */
type Stage = 'all' | 'setup' | 'live' | 'attention';

export function Fleet({
  onOpen,
  onCreate,
}: {
  onOpen: (tenantId: string) => void;
  onCreate: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const { config } = useConsole();

  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<Stage>('all');
  /**
   * The fixture roster (`t_test_*`) is hidden unless asked for — five tenants
   * every deploy reseeds for the contract check, which crowded the list the
   * owner opens to see businesses (2026-09-23). Filtered by the server, so the
   * pager's total and the rows agree.
   */
  const [showTest, setShowTest] = useState(false);
  const [sortByHealth, setSortByHealth] = useState(false);

  // Found by the server across the whole fleet, once the typing pauses.
  const searched = useSettled(search.trim());

  // "Needs attention" is a view of the page's health, not a server filter:
  // health is nine bounded reads per business and is only ever asked for the
  // page on screen. The note under the list says so when there is more than
  // one page.
  const status = stage === 'setup' ? 'onboarding,importing' : stage === 'live' ? 'live' : undefined;
  const filterKey = JSON.stringify([searched, status ?? '', showTest]);
  const [paged, setPaged] = useState({ key: filterKey, offset: 0 });
  const offset = paged.key === filterKey ? paged.offset : 0;

  const fleet = useRead(
    () =>
      api.fleet({
        limit: config.pageSize,
        offset,
        status,
        includeTest: showTest,
        search: searched,
      }),
    [filterKey, offset, config.pageSize],
  );

  /**
   * **The intake chip**, from its own cross-tenant read — an intake is a
   * conversation with a person, a different table and lifecycle from the
   * fleet's counts. A failed read is **silent**: it only sharpens the Next
   * line, and a list that reported an error because an optional hint did not
   * load would be a worse list.
   */
  const intakes = useRead(() => api.intakes(), []);
  const intakeBy = new Map<string, IntakeSummary>(
    (intakes.value?.intakes ?? []).map((row) => [row.tenant_id, row]),
  );

  const page = fleet.value;

  /**
   * **Health**, from its own read over the page on screen. A failure here is
   * *not* silent — health is evidence — and while it is missing no row says
   * "Nothing wrong": an unknown must not read as a good answer.
   */
  const pageIds = (page?.tenants ?? []).map((row) => row.tenant_id);
  const pageKey = pageIds.join(',');
  const health = useRead(
    () => (pageIds.length === 0 ? Promise.resolve(null) : api.fleetHealth(pageIds)),
    [pageKey],
  );
  const healthBy = new Map((health.value?.health ?? []).map((h) => [h.tenant_id, h]));

  const needsAttention = (row: FleetTenant): boolean => {
    const h = healthBy.get(row.tenant_id);
    return row.webhook_failing || (h !== undefined && h.verdict !== 'quiet');
  };
  const attentionCount = (page?.tenants ?? []).filter(needsAttention).length;

  const shown = (page?.tenants ?? []).filter((row) => stage !== 'attention' || needsAttention(row));
  const rows = sortByHealth
    ? [...shown].sort(
        (a, b) =>
          severity(healthBy.get(b.tenant_id), b) - severity(healthBy.get(a.tenant_id), a) ||
          a.tenant_id.localeCompare(b.tenant_id),
      )
    : shown;

  const onePage = page === null || page.total <= page.limit;

  return (
    <div className="view">
      <header className="bizhead">
        <div>
          <h2 className="bizhead__title">{t('businesses.title')}</h2>
          {page === null ? null : (
            <p className="muted">
              {t('businesses.count', { total: page.total })}
              {attentionCount > 0
                ? ` · ${t('businesses.needAttention', { count: attentionCount })}`
                : ''}
            </p>
          )}
        </div>
        <div className="bizhead__filters">
          <input
            className="bizhead__search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('businesses.search')}
            aria-label={t('businesses.search')}
          />
          <Segmented<Stage>
            label={t('businesses.stage')}
            options={[
              { id: 'all', label: t('businesses.stage.all') },
              { id: 'setup', label: t('businesses.stage.setup') },
              { id: 'live', label: t('businesses.stage.live') },
              { id: 'attention', label: t('businesses.stage.attention') },
            ]}
            value={stage}
            onChange={setStage}
          />
          <button type="button" className="button button--primary" onClick={onCreate}>
            {t('businesses.add')}
          </button>
        </div>
      </header>

      {fleet.failure !== null ? <Failure failure={fleet.failure} /> : null}

      {page === null ? (
        fleet.loading ? (
          <Loading what={t('loading.fleet')} />
        ) : null
      ) : (
        <>
          <div className="bizlist__bar">
            {health.failure !== null ? (
              <span className="bizlist__warn" title={health.failure.message}>
                {t('businesses.healthUnavailable')}
              </span>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="button button--sm"
              aria-pressed={sortByHealth}
              onClick={() => setSortByHealth((s) => !s)}
            >
              {t('businesses.sortWorst')}
            </button>
          </div>

          {rows.length === 0 ? (
            <Empty>
              {stage === 'attention'
                ? t('businesses.emptyAttention')
                : searched !== '' || stage !== 'all'
                  ? t('businesses.emptyFiltered')
                  : t('businesses.empty')}
            </Empty>
          ) : (
            <ul className="bizlist" aria-label={t('businesses.list')}>
              {rows.map((row) => (
                <BusinessRow
                  key={row.tenant_id}
                  row={row}
                  intake={intakeBy.get(row.tenant_id)}
                  health={healthBy.get(row.tenant_id)}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          )}

          {stage === 'attention' && !onePage ? (
            <p className="muted">{t('businesses.attentionPageOnly')}</p>
          ) : null}

          <div className="bizlist__foot">
            <label className="inline-field">
              <input
                type="checkbox"
                checked={showTest}
                onChange={(e) => setShowTest(e.target.checked)}
              />
              <span>{t('businesses.showTest')}</span>
            </label>
            <button
              type="button"
              className="button"
              onClick={fleet.reload}
              disabled={fleet.loading}
            >
              {t('common.refresh')}
            </button>
          </div>

          <Pager
            total={page.total}
            count={page.tenants.length}
            limit={page.limit}
            offset={page.offset}
            busy={fleet.loading}
            onOffset={(next) => setPaged({ key: filterKey, offset: next })}
          />
        </>
      )}
    </div>
  );
}

/**
 * One business. The whole row opens it; the name is also a real button, so
 * the row is reachable by keyboard without making the row itself a control
 * that contains another (the health chip is focusable for its reasons).
 */
function BusinessRow({
  row,
  intake,
  health,
  onOpen,
}: {
  row: FleetTenant;
  intake: IntakeSummary | undefined;
  health: TenantHealth | undefined;
  onOpen: (tenantId: string) => void;
}): ReactNode {
  const t = useT();
  const line = nextLine(
    t,
    nextStepOf({
      status: row.status,
      intake,
      health,
      webhookFailing: row.webhook_failing,
    }),
  );
  const marker = row.tenant_id.startsWith('t_demo_')
    ? t('businesses.demo')
    : row.tenant_id.startsWith('t_test_')
      ? t('businesses.test')
      : null;
  const lit = trackIndex(row.status);

  return (
    <li className="bizrow" onClick={() => onOpen(row.tenant_id)}>
      <div className="bizrow__who">
        <button
          type="button"
          className="bizrow__name"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(row.tenant_id);
          }}
        >
          {row.name === '' ? row.tenant_id : row.name}
        </button>
        <div className="bizrow__id">
          <span className="mono">{row.tenant_id}</span>
          <span aria-hidden="true"> · </span>
          <span title={row.timezone}>{cityOf(row.timezone)}</span>
          {marker === null ? null : (
            <>
              <span aria-hidden="true"> · </span>
              <span className="bizrow__marker">{marker}</span>
            </>
          )}
        </div>
      </div>

      <div className="bizrow__stage">
        <span className="bizrow__label">{t('businesses.col.stage')}</span>
        <strong>{stageName(t, row.status)}</strong>
        <span className="bizrow__bar" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <i key={i} className={lit >= 0 && i <= lit ? 'is-on' : undefined} />
          ))}
        </span>
      </div>

      <div className="bizrow__next">
        <span className="bizrow__label">{t('businesses.col.next')}</span>
        {line.lead === '' ? null : <b>{line.lead}</b>}
        {line.lead === '' || line.rest === '' ? null : ' '}
        {line.rest}
      </div>

      <div className="bizrow__health">
        <span className="bizrow__label">{t('businesses.col.health')}</span>
        <HealthChip health={health} webhookFailing={row.webhook_failing} tenantId={row.tenant_id} />
        {row.protected ? (
          <Chip tone="plain" title={t('business.protected.title')}>
            {t('businesses.protected')}
          </Chip>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The verdict as a chip, and why — the reasons in a small list that shows on
 * hover and on keyboard focus (the chip is focusable when there is a why), and
 * is the chip's description for a screen reader. Nothing until health has
 * answered: an unknown must not look like "Nothing wrong".
 */
function HealthChip({
  health,
  webhookFailing,
  tenantId,
}: {
  health: TenantHealth | undefined;
  webhookFailing: boolean;
  tenantId: string;
}): ReactNode {
  const t = useT();
  if (health === undefined) {
    return webhookFailing ? (
      <Chip tone="danger">{t('businesses.health.failing', { count: 1 })}</Chip>
    ) : (
      <span className="muted">—</span>
    );
  }
  const { tone, label } = verdictChip(t, health.verdict, health.reasons.length);
  if (health.reasons.length === 0) return <Chip tone={tone}>{label}</Chip>;
  const whyId = `why-${tenantId}`;
  return (
    <span
      className="bizhealth"
      tabIndex={0}
      aria-describedby={whyId}
      onClick={(e) => e.stopPropagation()}
    >
      <Chip tone={tone}>{label}</Chip>
      <span className="bizhealth__why" role="tooltip" id={whyId}>
        <ul>
          {health.reasons.map((r) => (
            <li key={r}>{reasonLabel(t, r)}</li>
          ))}
        </ul>
      </span>
    </span>
  );
}

function verdictChip(
  t: T,
  verdict: HealthVerdict,
  reasons: number,
): { tone: ChipTone; label: string } {
  switch (verdict) {
    case 'quiet':
      return { tone: 'ok', label: t('businesses.health.ok') };
    case 'attention':
      return { tone: 'warn', label: t('businesses.health.attention', { count: reasons }) };
    case 'failing':
      return { tone: 'danger', label: t('businesses.health.failing', { count: reasons }) };
  }
}

/** Worst first when sorted; unknown sorts below "nothing wrong", not above it. */
function severity(health: TenantHealth | undefined, row: FleetTenant): number {
  const order: Record<HealthVerdict, number> = { failing: 3, attention: 2, quiet: 1 };
  if (health === undefined) return row.webhook_failing ? 3 : 0;
  return order[health.verdict];
}

/** "St Thomas" from `America/St_Thomas` — the business's place, as far as the record knows. */
function cityOf(timezone: string): string {
  return (timezone.split('/').pop() ?? timezone).replace(/_/g, ' ');
}

/**
 * Offset paging, off the response rather than the request.
 *
 * The server clamps `limit` silently and echoes what it actually used, so the
 * page controls read what came back — a console that paged by its own
 * arithmetic would skip rows the moment somebody raised `PAGE_SIZE_MAX`.
 *
 * Only the fleet listing carries a `total`; the delivery log and the audit log
 * do not, because counting an append-only table that grows forever is the
 * unbounded read spec §5.5.5 rules out. So the end of the list is inferred the
 * only honest way available — a short page — and `count` is how the caller says
 * how many rows it actually got.
 */
export function Pager({
  total,
  count,
  limit,
  offset,
  busy,
  onOffset,
}: {
  total?: number | undefined;
  count?: number | undefined;
  limit: number;
  offset: number;
  busy: boolean;
  onOffset: (offset: number) => void;
}): ReactNode {
  const t = useT();
  const atEnd =
    total !== undefined ? offset + limit >= total : count !== undefined && count < limit;
  // Row ordinals and a row total: integers an operator reads as positions in a
  // list, not as quantities, so they are rendered as digits rather than pushed
  // through `Intl.NumberFormat`. Grouping them would put a separator inside a
  // range (`1.001–1.050`) and buy nothing at page sizes the server clamps.
  const from = String(offset + 1);
  const to = String(offset + limit);
  return (
    <div className="pager">
      <button
        type="button"
        className="button"
        disabled={busy || offset === 0}
        onClick={() => onOffset(Math.max(0, offset - limit))}
      >
        {t('pager.previous')}
      </button>
      <span className="muted">
        {total === undefined
          ? t('pager.range', { from, to })
          : t('pager.rangeOf', { from, to, total: String(total) })}
      </span>
      <button
        type="button"
        className="button"
        disabled={busy || atEnd}
        onClick={() => onOffset(offset + limit)}
      >
        {t('pager.next')}
      </button>
    </div>
  );
}
